import { styled } from '@mui/material/styles'
import { parse, isAfter, isBefore, format } from 'date-fns'
import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  getAppointmentDetails,
  scheduleAppointment,
  ApiError,
  trackAppointmentAccess,
} from '../../api'
import {
  formatOsaTimeRange,
  getOsaTimeRanges,
  isOsaTimeRangeKey,
} from '../../utils/timeRange.utils'
import type { OsaTimeRange } from '../../utils/timeRange.utils'

interface AppointmentDetails {
  scoutName: string
  customerName: string
  address: string
  residentialUnits: number
  date: string
  timeSlot: string
  scheduleStartTime?: string | null
}

const PageContainer = styled('div')({
  minHeight: '100vh',
  fontFamily: '"Poppins", Helvetica, sans-serif',
  fontSize: '0.9rem',
  color: '#181d29',
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '1rem',
})

const ContentWrapper = styled('div')({
  maxWidth: '410px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.2rem',
})

const Title = styled('h3')({
  //  fontSize: '1.25rem',
  fontWeight: 600,
  textAlign: 'center',
  margin: '0 0 2rem 0',
  color: '#181d29',
})

const DetailsSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  marginBottom: '2rem',
})

const SectionHeader = styled('h2')({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: '#181d29',
  margin: '0 0 0.5rem 0',
})

const GreetingText = styled('p')({
  fontSize: '0.9rem',
  color: '#181d29',
  lineHeight: 1.6,
  marginBottom: '0.5rem',
})

const DetailRow = styled('div')({
  fontSize: '0.9rem',
  color: '#181d29',
  lineHeight: 1.4,
})

const FormSection = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
})

const TimeInputWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
})

const InputGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.3rem',
})

const Label = styled('label')({
  fontSize: '0.9rem',
  fontWeight: 500,
  color: '#181d29',
})

const TimeInput = styled('input')({
  padding: '0.3rem 0.5rem',
  border: '1.5px solid #767676',
  borderRadius: '6px',
  fontSize: '0.9rem',
  fontFamily: '"Poppins", Helvetica, sans-serif',
  color: '#181d29',
  backgroundColor: '#fff',
  '&:focus': {
    outline: 'none',
    borderColor: '#2a89cd',
  },
  '&:disabled': {
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
  },
})

const CheckboxWrapper = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
  margin: '1rem 0',
})

const CheckboxInput = styled('input')({
  marginTop: '0.1rem',
  transform: 'scale(1.1)',
})

const CheckboxLabel = styled('label')({
  fontSize: '0.9rem',
  color: '#181d29',
  lineHeight: 1.4,
  cursor: 'pointer',
})

const SubmitButton = styled('button')({
  width: '100%',
  padding: '0.75rem 0',
  backgroundColor: '#181d29',
  color: '#f5f5f7',
  border: 'none',
  borderRadius: '18px',
  fontSize: '0.9rem',
  fontWeight: 500,
  fontFamily: '"Poppins", Helvetica, sans-serif',
  cursor: 'pointer',
  boxShadow: '0 3px 9px rgba(0,0,0,0.18)',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '#2a89cd',
  },
  '&:disabled': {
    backgroundColor: '#767676',
    cursor: 'not-allowed',
  },
})

const ErrorMessage = styled('div')({
  backgroundColor: '#ffebee',
  color: '#c62828',
  padding: '0.75rem',
  borderRadius: '6px',
  fontSize: '0.9rem',
  border: '1px solid #ffcdd2',
  marginBottom: '1rem',
})

const ErrorPageContainer = styled('div')({
  textAlign: 'center',
  padding: '2rem 1rem',
})

const ErrorTitle = styled('h2')({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: '#181d29',
  marginBottom: '1.5rem',
})

const ErrorDescription = styled('p')({
  fontSize: '0.9rem',
  color: '#181d29',
  lineHeight: 1.6,
  marginBottom: '1rem',
})

const SuccessMessage = styled('div')({
  backgroundColor: '#e8f5e8',
  color: '#2e7d32',
  padding: '1rem',
  borderRadius: '6px',
  fontSize: '0.9rem',
  border: '1px solid #c8e6c9',
  textAlign: 'center',
})

const LoadingMessage = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
  padding: '2rem',
  fontSize: '0.9rem',
  color: '#181d29',
})

const DisclaimerText = styled('div')({
  fontSize: '0.8rem',
  color: '#767676',
  lineHeight: 1.4,
  marginTop: '1rem',
  textAlign: 'center',
})

const LogoContainer = styled('div')({
  display: 'block',
  margin: '0 auto 0rem auto',
  width: '180px',
})

const Logo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    aria-label="Logo"
    className="logo"
    viewBox="0 0 180 51"
  >
    <g fill="#181D29" clipPath="url(#a)">
      <path d="M151.463 37c-2.303 8.06-9.287 13.51-19.116 13.51-11.208 0-20.266-9.211-20.266-20.65 0-11.44 9.058-20.805 20.266-20.805 9.519 0 17.503 5.987 19.423 16.581l-27.943 11.747c1.996 2.685 4.989 4.223 8.445 4.223 3.455 0 6.677-1.46 8.366-4.606h10.825Zm-11.361-15.507c-1.843-2.227-4.606-3.531-7.83-3.531-5.835 0-10.518 4.606-10.748 11.361l18.578-7.83ZM10.287 33.777c1.306 4.835 5.142 7.83 9.901 7.83 3.378 0 6.68-1.46 8.369-4.605h10.825c-2.303 8.059-9.29 13.51-19.116 13.51C9.136 50.512 0 41.3 0 29.863 0 18.426 9.136 9.06 20.266 9.06c11.13 0 21.341 9.058 19.884 24.718H10.287Zm19.805-8.29c-1.228-4.606-5.066-7.523-9.904-7.523-4.837 0-8.29 2.917-9.748 7.523h19.652ZM81.757 49.591H71.624V26.483c0-2.303-.69-7.83-7.37-7.83-4.53 0-7.676 3.378-7.676 7.83v23.108H46.445V10.058h9.672v3.838c2.535-2.993 5.988-4.837 10.211-4.837 9.288 0 15.431 6.986 15.431 17.427v23.107l-.002-.002ZM108.627 49.515c-11.592 1.229-17.734-5.758-17.734-16.581V19.04h-4.76v-8.982h4.76V0h10.133v10.057h7.141v8.983h-7.141v13.972c0 5.527 2.535 7.6 7.601 7.294v9.212-.003ZM180 19.117c-4.07-.46-7.447 0-9.75 2.456-2.15 2.228-2.535 5.452-2.535 8.598v19.423h-10.133V10.06h9.673v3.913c3.224-3.991 7.983-5.449 12.743-4.681v9.826H180Z" />
    </g>
    <defs>
      <clipPath id="a">
        <path fill="#fff" d="M0 0h180v50.512H0z" />
      </clipPath>
    </defs>
  </svg>
)

const ScoutConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDetails | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<OsaTimeRange | null>(null)
  const [apiError, setApiError] = useState<'not_available' | 'generic' | null>(null)
  const [appointmentStatus, setAppointmentStatus] = useState<string | null>(null)

  const appointmentUuid = searchParams.get('appointmentUuid') || ''
  const scoutUuid = searchParams.get('scoutUuid') || ''
  const option = searchParams.get('option') ? parseInt(searchParams.get('option')!, 10) : null

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  useEffect(() => {
    const fetchAppointmentDetails = async () => {
      if (!appointmentUuid || !scoutUuid || option === null) {
        setError('Fehlende Parameter: appointmentUuid, scoutUuid oder option.')
        setInitialLoading(false)
        return
      }

      try {
        const apiResponse = await getAppointmentDetails(appointmentUuid, scoutUuid, option)
        const mappedDetails: AppointmentDetails = {
          scoutName: apiResponse.data.scoutName,
          customerName: apiResponse.data.customerName,
          address: apiResponse.data.customerAddress,
          residentialUnits: apiResponse.data.residentialUnits,
          date: formatDate(apiResponse.data.optionDate),
          timeSlot: formatOsaTimeRange(
            apiResponse.data.optionTimeRange,
            new Date(apiResponse.data.optionDate)
          ),
          scheduleStartTime: apiResponse.data.scheduleStartTime,
        }
        setAppointmentDetails(mappedDetails)
        setAppointmentStatus(apiResponse.data.status)

        // Set the time range key and initial start time
        const rangeKey = apiResponse.data.optionTimeRange
        if (isOsaTimeRangeKey(rangeKey)) {
          const range = getOsaTimeRanges(new Date(apiResponse.data.optionDate))[rangeKey]
          setTimeRange(range)

          // If appointment is dispatched and has a scheduled start time, use that
          if (apiResponse.data.status === 'dispatched' && apiResponse.data.scheduleStartTime) {
            try {
              const scheduledDate = new Date(apiResponse.data.scheduleStartTime)
              const hours = scheduledDate.getHours().toString().padStart(2, '0')
              const minutes = scheduledDate.getMinutes().toString().padStart(2, '0')
              const scheduledTime = `${hours}:${minutes}`
              setStartTime(scheduledTime)
              setEndTime(calculateEndTime(scheduledTime))
            } catch (error) {
              console.error('Error parsing scheduled time:', error)
              // Fallback to default time
              const { defaultStart } = range
              setStartTime(defaultStart)
              setEndTime(calculateEndTime(defaultStart))
            }
          } else {
            const { defaultStart } = range
            setStartTime(defaultStart)
            setEndTime(calculateEndTime(defaultStart))
          }
        }

        setError('')

        // Track appointment access
        trackAppointmentAccess(appointmentUuid, scoutUuid).catch(error => {
          console.warn('Failed to track appointment access:', error)
          // Don't block the UI if tracking fails
        })
      } catch (error) {
        console.error('Error fetching appointment details:', error)
        // Check if this is a 409 error indicating appointment is not available
        if (error instanceof ApiError && error.status === 409) {
          setApiError('not_available')
        } else {
          setApiError('generic')
          setError('Fehler beim Laden der Termindaten. Bitte versuchen Sie es erneut.')
        }
      } finally {
        setInitialLoading(false)
      }
    }

    fetchAppointmentDetails()
  }, [appointmentUuid, scoutUuid, option])

  const calculateEndTime = (start: string) => {
    if (!start) return ''
    try {
      const referenceDate = new Date()
      const startTime = parse(start, 'HH:mm', referenceDate)
      // Add 1 hour and 30 minutes
      startTime.setHours(startTime.getHours() + 1)
      startTime.setMinutes(startTime.getMinutes() + 30)
      return format(startTime, 'HH:mm')
    } catch (error) {
      console.error('Error calculating end time:', error)
      return ''
    }
  }

  const handleStartTimeChange = (value: string) => {
    if (!value || !timeRange) {
      setStartTime(value)
      setEndTime(calculateEndTime(value))
      setError('')
      return
    }

    // Get the time range constraints
    const range = timeRange
    let clampedTime = value

    try {
      // Parse times using date-fns (using a reference date for comparison)
      const referenceDate = new Date()
      const inputTime = parse(value, 'HH:mm', referenceDate)
      const minTime = parse(range.minTime, 'HH:mm', referenceDate)
      const maxTime = parse(range.maxTime, 'HH:mm', referenceDate)

      // Clamp to minimum if too early
      if (isBefore(inputTime, minTime)) {
        clampedTime = range.minTime
      }
      // Clamp to maximum if too late
      else if (isAfter(inputTime, maxTime)) {
        clampedTime = range.maxTime
      }
    } catch (error) {
      // If parsing fails, use the original value
      console.error('Error parsing time:', error)
    }

    setStartTime(clampedTime)
    setEndTime(calculateEndTime(clampedTime))
    setError('')
  }

  const validateTime = (time: string) => {
    if (!time || !timeRange) return false

    try {
      const range = timeRange
      const referenceDate = new Date()
      const inputTime = parse(time, 'HH:mm', referenceDate)
      const minTime = parse(range.minTime, 'HH:mm', referenceDate)
      const maxTime = parse(range.maxTime, 'HH:mm', referenceDate)

      return !isBefore(inputTime, minTime) && !isAfter(inputTime, maxTime)
    } catch (error) {
      console.error('Error validating time:', error)
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!startTime || !endTime) {
      setError('Bitte geben Sie Start- und Endzeit ein.')
      return
    }

    if (!validateTime(startTime)) {
      if (timeRange) {
        setError(
          `Die Startzeit muss zwischen ${timeRange.minTime} und ${timeRange.maxTime} Uhr liegen.`
        )
      } else {
        setError('Ungültiger Zeitbereich.')
      }
      return
    }

    if (!confirmed) {
      setError('Bitte bestätigen Sie, dass Sie den Termin verbindlich übernehmen.')
      return
    }

    if (!appointmentUuid || !scoutUuid || option === null) {
      setError('Fehlende Parameter: appointmentUuid, scoutUuid oder option.')
      return
    }

    if (!appointmentDetails?.date) {
      setError('Termindatum ist nicht verfügbar.')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Parse the German date format (DD.MM.YYYY) and combine with selected time
      const [day, month, year] = appointmentDetails.date.split('.')
      const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      // Parse the time (HH:MM) and set it on the date
      const [hours, minutes] = startTime.split(':').map(Number)
      dateObj.setHours(hours, minutes, 0, 0)
      // Convert to ISO string for the API
      const scheduleStartTime = dateObj.toISOString()

      const result = await scheduleAppointment(appointmentUuid, {
        appointmentUUID: appointmentUuid, // Note: Backend expects 3 U's
        scoutUUID: scoutUuid,
        option,
        scheduleStartTime,
      })

      if (result.code === 200) {
        setSuccess(true)
        // Appointment scheduled successfully
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.')
      }
    } catch (error) {
      console.error('Error scheduling appointment:', error)
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.')
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <LoadingMessage>
            <div>Lade Termindaten...</div>
          </LoadingMessage>
        </ContentWrapper>
      </PageContainer>
    )
  }

  // Show error page if appointment is not available (409 error)
  if (apiError === 'not_available') {
    return (
      <PageContainer>
        <ContentWrapper>
          <LogoContainer>
            <Logo />
          </LogoContainer>
          <Title>Termin bestätigen</Title>
          <ErrorPageContainer>
            <ErrorTitle>Termin leider nicht mehr verfügbar</ErrorTitle>
            <ErrorDescription>
              Vielen Dank für Ihr Interesse. Dieser Termin wurde bereits von einem anderen Scout
              gebucht und steht daher nicht mehr zur Verfügung.
            </ErrorDescription>
          </ErrorPageContainer>
        </ContentWrapper>
      </PageContainer>
    )
  }

  // Show generic error if other API errors occurred
  if (apiError === 'generic') {
    return (
      <PageContainer>
        <ContentWrapper>
          <Title>Termin bestätigen</Title>
          <ErrorMessage>
            {error || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'}
          </ErrorMessage>
        </ContentWrapper>
      </PageContainer>
    )
  }

  if (success) {
    return (
      <PageContainer>
        <ContentWrapper>
          <SuccessMessage>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
              Termin erfolgreich bestätigt!
            </div>
            <div>Ihr Termin wurde erfolgreich bestätigt. Sie erhalten eine Bestätigungsmail.</div>
          </SuccessMessage>
        </ContentWrapper>
      </PageContainer>
    )
  }

  // Check if appointment is already dispatched (read-only mode)
  const isDispatched = appointmentStatus === 'dispatched'

  return (
    <PageContainer>
      <ContentWrapper>
        <LogoContainer>
          <Logo />
        </LogoContainer>
        <Title>Termin bestätigen</Title>
        <SectionHeader>Termindetails</SectionHeader>
        {!isDispatched && (
          <GreetingText>
            Hallo {appointmentDetails?.scoutName || 'Scout'},
            <br />
            es liegt ein neuer Terminvorschlag vor
          </GreetingText>
        )}
        <DetailsSection>
          <DetailRow>
            <strong>Kunde:</strong> {appointmentDetails?.customerName || 'Laden...'}
          </DetailRow>
          <DetailRow>
            <strong>Adresse:</strong> {appointmentDetails?.address || 'Laden...'}
          </DetailRow>
          <DetailRow>
            <strong>Anzahl Wohneinheiten:</strong>{' '}
            {appointmentDetails?.residentialUnits || 'unbekannt'}
          </DetailRow>
          {!isDispatched && (
            <>
              <DetailRow>
                <strong>Datum:</strong> {appointmentDetails?.date || 'Laden...'}
              </DetailRow>
              <DetailRow>
                <strong>Verfügbarkeit:</strong> {appointmentDetails?.timeSlot || 'Laden...'}
              </DetailRow>
            </>
          )}
        </DetailsSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {isDispatched ? (
          <>
            <SuccessMessage>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                Termin bereits bestätigt
              </div>
              <div>Dieser Termin wurde bereits bestätigt und ist in Bearbeitung.</div>
            </SuccessMessage>

            <DetailsSection style={{ marginTop: '1.5rem' }}>
              <DetailRow>
                <strong>Bestätigter Termin:</strong>{' '}
                {appointmentDetails?.scheduleStartTime
                  ? (() => {
                      try {
                        const date = new Date(appointmentDetails.scheduleStartTime)
                        const dateStr = date.toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })
                        const timeStr = date.toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                        return `${dateStr} um ${timeStr} Uhr`
                      } catch {
                        return 'Termin bestätigt'
                      }
                    })()
                  : 'Termin bestätigt'}
              </DetailRow>
            </DetailsSection>
          </>
        ) : (
          <FormSection onSubmit={handleSubmit}>
            <SectionHeader>Termin Zeitraum</SectionHeader>
            <TimeInputWrapper>
              <InputGroup>
                <Label htmlFor="startTime">Beginn</Label>
                <TimeInput
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={e => handleStartTimeChange(e.target.value)}
                  min={timeRange?.minTime ?? '09:00'}
                  max={timeRange?.maxTime ?? '17:00'}
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="endTime">Ende</Label>
                <TimeInput id="endTime" type="time" value={endTime} disabled />
              </InputGroup>
            </TimeInputWrapper>

            <CheckboxWrapper>
              <CheckboxInput
                id="confirmation"
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
              />
              <CheckboxLabel htmlFor="confirmation">
                Ich bestätige, dass ich den Termin verbindlich übernehme
              </CheckboxLabel>
            </CheckboxWrapper>

            <SubmitButton type="submit" disabled={!startTime || !endTime || !confirmed || loading}>
              {loading ? 'Wird bestätigt...' : 'Termin bestätigen'}
            </SubmitButton>

            <DisclaimerText>
              Indem Sie auf Termin bestätigen klicken, übernehmen Sie verbindlich den Vor-Ort-Termin
              am {appointmentDetails?.date || '05.07.2025'} zu dem von Ihnen festgelegten Zeitpunkt.
              Sie erhalten ebenfalls eine Bestätigungs Email mit weiteren Details.
            </DisclaimerText>
          </FormSection>
        )}
      </ContentWrapper>
    </PageContainer>
  )
}

export default ScoutConfirmation
